import { useEffect, useState } from 'react';
import firebase from 'firebase/app';

/**
 * The customer's names for the four storage labels (2024 brief #12, Admin request #3; 5 Nov 2026).
 *
 * Names only. Each file keeps its stored value (preview / master / CAD / distribution), so renaming
 * a label changes no file record, and the storage rule is unchanged: the first label (stored as
 * "preview") is hot storage and playable; the other three are cold storage.
 *
 * Stored in settings/labels (everyone with a role reads, admins write, per the database rules).
 * One shared listener, started from the router after sign-in; every screen reads it through
 * useLabelNames() and updates straight away when an admin saves new names.
 */
export type LabelKey = 'preview' | 'master' | 'CAD' | 'distribution';
export type LabelNames = Record<LabelKey, string>;

export const LABEL_KEYS: LabelKey[] = ['preview', 'master', 'CAD', 'distribution'];
export const LABEL_DEFAULTS: LabelNames = { preview: 'Preview', master: 'Master', CAD: 'Approval', distribution: 'Distribution' };
export const LABEL_MAX = 20;

export const cleanLabels = (raw: any): LabelNames => {
  const out = { ...LABEL_DEFAULTS };
  LABEL_KEYS.forEach((k) => {
    const v = raw && typeof raw[k] === 'string' ? raw[k].replace(/\s+/g, ' ').trim().slice(0, LABEL_MAX) : '';
    if (v) out[k] = v;
  });
  return out;
};

let current: LabelNames = LABEL_DEFAULTS;
const subscribers = new Set<(n: LabelNames) => void>();
let stop: (() => void) | null = null;

const publish = (n: LabelNames) => {
  current = n;
  subscribers.forEach((fn) => fn(n));
};

/** A label's display name; falls back to the stored value for anything unexpected. */
export const labelName = (value: string, names: LabelNames = current) => (names as any)[value] || value;

/** Mount once, after sign-in (router), next to the colours. */
export const useLabelNamesListener = () => {
  useEffect(() => {
    if (stop) return undefined;
    stop = firebase
      .firestore()
      .collection('settings')
      .doc('labels')
      .onSnapshot(
        (d) => publish(cleanLabels(d.data())),
        () => undefined, // no access (e.g. a client login): keep the defaults
      );
    return () => {
      if (stop) stop();
      stop = null;
    };
  }, []);
};

/** The current names; re-renders when an admin saves new ones. */
const useLabelNames = (): LabelNames => {
  const [names, setNames] = useState<LabelNames>(current);
  useEffect(() => {
    subscribers.add(setNames);
    setNames(current);
    return () => {
      subscribers.delete(setNames);
    };
  }, []);
  return names;
};

export default useLabelNames;
